import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, GraduationCap, CheckCircle2, Clock, Trash2, AlertCircle,
  Users, Search
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const categoryConfig = {
  awareness: { labelKey: "training.cat.awareness", color: "text-blue-500" },
  phishing: { labelKey: "training.cat.phishing", color: "text-red-500" },
  data_protection: { labelKey: "training.cat.data_protection", color: "text-green-500" },
  incident_response: { labelKey: "training.cat.incident_response", color: "text-orange-500" },
  gdpr: { labelKey: "training.cat.gdpr", color: "text-purple-500" },
  access_control: { labelKey: "training.cat.access_control", color: "text-teal-500" },
  other: { labelKey: "training.cat.other", color: "text-gray-500" }
};

const TrainingModule = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [trainings, setTrainings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newCategory, setNewCategory] = useState("awareness");
  const [newDueDate, setNewDueDate] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/trainings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/trainings/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTrainings(tRes.data);
      setSummary(sRes.data);
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createTraining = async () => {
    if (!newTitle) { toast.error("Title required"); return; }
    try {
      let url = `${API}/trainings?title=${encodeURIComponent(newTitle)}&category=${newCategory}&target_roles=all`;
      if (newDueDate) url += `&due_date=${newDueDate}`;
      if (newDesc) url += `&description=${encodeURIComponent(newDesc)}`;
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Training created");
      setShowCreate(false);
      setNewTitle(""); setNewDesc(""); setNewCategory("awareness"); setNewDueDate("");
      fetchAll();
    } catch (error) {
      toast.error("Failed to create training");
    }
  };

  const completeTraining = async (trainingId) => {
    try {
      const res = await axios.post(`${API}/trainings/${trainingId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.status === "already_completed" ? "Already completed" : "Training completed!");
      fetchAll();
    } catch (error) {
      toast.error("Failed");
    }
  };

  const deleteTraining = async (trainingId) => {
    try {
      await axios.delete(`${API}/trainings/${trainingId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      fetchAll();
    } catch (error) {
      toast.error("Failed");
    }
  };

  const today = new Date().toISOString().split("T")[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="training-page">
      <PageHeader
        title={t("training.title")}
        description={t("training.subtitle")}
        actions={
          <Button onClick={() => setShowCreate(true)} data-testid="create-training-btn">
            <Plus className="w-4 h-4 mr-2" /> {t("training.new")}
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-2xl font-heading font-bold">{summary?.total_trainings || 0}</p>
          <p className="text-xs text-muted-foreground">{t("training.programs")}</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-2xl font-heading font-bold text-green-500">{summary?.active || 0}</p>
          <p className="text-xs text-muted-foreground">{t("active")}</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-2xl font-heading font-bold text-red-500">{summary?.overdue || 0}</p>
          <p className="text-xs text-muted-foreground">{t("overdue")}</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-2xl font-heading font-bold text-blue-500">{summary?.total_completions || 0}</p>
          <p className="text-xs text-muted-foreground">{t("training.completions")}</p>
        </CardContent></Card>
        <Card className="border-border"><CardContent className="pt-4 pb-4">
          <p className="text-2xl font-heading font-bold">{summary?.completion_rate || 0}%</p>
          <p className="text-xs text-muted-foreground">{t("training.completion_rate")}</p>
          <Progress value={summary?.completion_rate || 0} className="h-1.5 mt-2" />
        </CardContent></Card>
      </div>

      {/* Training List */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {trainings.map((t, i) => {
            const isOverdue = t.due_date && t.due_date < today && t.status === "active";
            const myCompletion = t.completions?.find(c => c.user_id);
            return (
              <motion.div key={t.training_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <Card className={`border-border ${isOverdue ? 'border-red-500/50' : ''}`} data-testid={`training-card-${t.training_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <GraduationCap className={`w-8 h-8 flex-shrink-0 ${categoryConfig[t.category]?.color || 'text-gray-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="secondary" className="capitalize">{categoryConfig[t.category]?.labelKey ? t(categoryConfig[t.category].labelKey) : t.category}</Badge>
                            {isOverdue && <Badge variant="destructive">{t("overdue")}</Badge>}
                            <Badge variant="outline">{t.completions?.length || 0} {t("training.completions").toLowerCase()}</Badge>
                          </div>
                          <p className="font-medium text-sm">{t.title}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            {t.due_date && <span>{t("maint.due_date")}: {new Date(t.due_date).toLocaleDateString()}</span>}
                            <span>{t("training.roles")}: {t.target_roles?.join(", ")}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" onClick={() => completeTraining(t.training_id)} data-testid={`complete-training-${t.training_id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1 text-green-500" /> {t("training.complete_btn")}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteTraining(t.training_id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
        {trainings.length === 0 && (
          <div className="text-center py-12">
            <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t("training.no_programs")}</p>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t("training.new_program_title")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("title")} *</Label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g., Q1 Security Awareness" data-testid="training-title-input" />
            </div>
            <div className="space-y-2">
              <Label>{t("description")}</Label>
              <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder={t("training.desc_ph")} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("category")}</Label>
                <Select value={newCategory} onValueChange={setNewCategory}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryConfig).map(([k, v]) => <SelectItem key={k} value={k}>{t(v.labelKey)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("maint.due_date")}</Label>
                <Input type="date" value={newDueDate} onChange={(e) => setNewDueDate(e.target.value)} data-testid="training-due-date" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel")}</Button>
            <Button onClick={createTraining} data-testid="save-training-btn">{t("create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainingModule;
